"use client";

/**
 * RichTextEditor Component
 *
 * Requirements Summary:
 * - TipTap-based rich text editor with formatting toolbar
 * - Optional policy placeholder support: colored tokens, sticky insert bar, inline autocomplete
 * - Exposes insertText and focus via ref for parent components
 *
 * Related files:
 * - /components/form/fields/rich-text-extensions/ - PlaceholderHighlight, PlaceholderSuggestion
 * - /utils/policies/policyPlaceholders.ts - Placeholder definitions
 */

import React, { useImperativeHandle, forwardRef, useMemo, useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { PlaceholderHighlight, createPlaceholderSuggestion } from './rich-text-extensions';
import type { PlaceholderItem } from './rich-text-extensions';

export interface RichTextEditorRef {
  insertText: (text: string) => void;
  focus: () => void;
}

interface RichTextEditorProps {
  id?: string;
  label?: string;
  hideLabel?: boolean;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
  minHeight?: string;
  /** When provided, enables placeholder highlighting, inline autocomplete, and sticky insert bar */
  placeholders?: PlaceholderItem[];
  /** Called after a placeholder is inserted via the bar or autocomplete */
  onInsertPlaceholder?: (code: string) => void;
}

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

function ToolbarButton({ onClick, isActive, disabled, title, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`
        px-2.5 py-1.5 rounded text-sm font-medium transition-colors
        ${isActive
          ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline insert confirmation (fades out after 2s)
// ─────────────────────────────────────────────────────────────────────────────

function InsertConfirmation({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <span
      key={text + Date.now()}
      className="inline-flex items-center gap-1 text-xs text-violet-700 dark:text-violet-300 animate-fade-out"
    >
      <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
      Inserted {text}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(function RichTextEditor({
  id,
  label,
  hideLabel = false,
  value,
  onChange,
  placeholder = 'Start typing...',
  hint,
  disabled = false,
  minHeight = '300px',
  placeholders,
  onInsertPlaceholder,
}, ref) {
  const [lastInserted, setLastInserted] = useState<string | null>(null);
  const [insertKey, setInsertKey] = useState(0);

  // Build extensions list — memoize to avoid re-creating on every render
  const extensions = useMemo(() => {
    const exts = [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ];

    if (placeholders && placeholders.length > 0) {
      exts.push(PlaceholderHighlight as any);
      exts.push(createPlaceholderSuggestion(placeholders) as any);
    }

    return exts;
  }, [placeholder, placeholders]);

  const editor = useEditor({
    extensions,
    content: value,
    editable: !disabled,
    immediatelyRender: false, // Required for Next.js SSR compatibility
    onUpdate: ({ editor }) => {
      // Get HTML content for storage to preserve formatting
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm dark:prose-invert max-w-none focus:outline-none min-h-[inherit] p-4',
      },
    },
  });

  // Show a brief confirmation when a placeholder is inserted
  const showInsertConfirmation = useCallback((code: string) => {
    setLastInserted(code);
    setInsertKey((k) => k + 1);
    // Clear after animation
    const timer = setTimeout(() => setLastInserted(null), 2000);
    return () => clearTimeout(timer);
  }, []);

  // Insert a placeholder via the toolbar buttons
  const handleInsertPlaceholder = useCallback(
    (code: string) => {
      if (!editor) return;
      editor.chain().focus().insertContent(code).run();
      showInsertConfirmation(code);
      onInsertPlaceholder?.(code);
    },
    [editor, showInsertConfirmation, onInsertPlaceholder],
  );

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    insertText: (text: string) => {
      if (editor) {
        editor.chain().focus().insertContent(text).run();
      }
    },
    focus: () => {
      if (editor) {
        editor.chain().focus().run();
      }
    },
  }), [editor]);

  // Update editor content when value changes externally
  // Compare HTML to HTML to avoid triggering unnecessary updates
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      // Use emitUpdate: false to prevent triggering onChange during external sync
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="space-y-1.5">
      {label && !hideLabel && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
        </label>
      )}

      <div className="rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900">
        {/* Formatting Toolbar */}
        <div className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            disabled={disabled}
            title="Bold"
          >
            <span className="font-bold">B</span>
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            disabled={disabled}
            title="Italic"
          >
            <span className="italic">I</span>
          </ToolbarButton>

          <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            isActive={editor.isActive('heading', { level: 2 })}
            disabled={disabled}
            title="Heading"
          >
            H
          </ToolbarButton>

          <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            disabled={disabled}
            title="Bullet List"
          >
            &bull; List
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            disabled={disabled}
            title="Numbered List"
          >
            1. List
          </ToolbarButton>

          <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />

          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={disabled || !editor.can().undo()}
            title="Undo"
          >
            &#x21A9;
          </ToolbarButton>

          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={disabled || !editor.can().redo()}
            title="Redo"
          >
            &#x21AA;
          </ToolbarButton>
        </div>

        {/* Placeholder Insert Bar — sticky below toolbar */}
        {placeholders && placeholders.length > 0 && (
          <div className="border-b border-gray-200 dark:border-gray-700 bg-violet-50/60 dark:bg-violet-950/20 px-3 py-2 sticky top-0 z-10">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-xs font-medium text-violet-800 dark:text-violet-200 uppercase tracking-wider shrink-0">
                Insert Variable
              </span>
              <span className="text-xs text-violet-500 dark:text-violet-400">
                Click to insert at cursor, or type <kbd className="px-1 py-0.5 rounded bg-violet-100 dark:bg-violet-900/40 font-mono text-[10px]">[</kbd> in the editor
              </span>
              <div className="ml-auto">
                <InsertConfirmation key={insertKey} text={lastInserted} />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {placeholders.map((p) => (
                <button
                  key={p.code}
                  type="button"
                  onClick={() => handleInsertPlaceholder(p.code)}
                  disabled={disabled}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md
                    bg-violet-100 dark:bg-violet-900/40
                    border border-violet-200 dark:border-violet-800
                    text-xs font-mono font-medium text-violet-700 dark:text-violet-300
                    hover:bg-violet-200 dark:hover:bg-violet-900/60
                    hover:border-violet-300 dark:hover:border-violet-700
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-colors cursor-pointer"
                  title={p.desc}
                >
                  {p.code}
                  <svg className="w-3 h-3 opacity-50" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
                  </svg>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Editor Content */}
        <div
          style={{ minHeight }}
          className="cursor-text"
          onClick={() => editor.chain().focus().run()}
        >
          <EditorContent editor={editor} />
        </div>
      </div>

      {hint && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      )}

      {/* Placeholder token styles — injected once when placeholders are enabled */}
      {placeholders && placeholders.length > 0 && (
        <style dangerouslySetInnerHTML={{ __html: `
          .policy-placeholder-token {
            background-color: rgb(237 233 254);
            color: rgb(109 40 217);
            border: 1px solid rgb(196 181 253);
            border-radius: 4px;
            padding: 1px 4px;
            font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace;
            font-size: 0.875em;
            font-weight: 500;
            white-space: nowrap;
          }
          .dark .policy-placeholder-token {
            background-color: rgb(109 40 217 / 0.2);
            color: rgb(196 181 253);
            border-color: rgb(109 40 217 / 0.4);
          }
          @keyframes placeholder-fade-out {
            0% { opacity: 1; }
            70% { opacity: 1; }
            100% { opacity: 0; }
          }
          .animate-fade-out {
            animation: placeholder-fade-out 2s ease-out forwards;
          }
        ` }} />
      )}
    </div>
  );
});

export default RichTextEditor;
